/** REQ-059 T3 — selector de Process del proyecto (PROJECT → PROCESS → TASK). Obligatorio al crear tareas; permite crear uno nuevo en línea. */
import { useCallback, useEffect, useState } from 'react';
import { workApi, type ProcessRow } from '../../stores/workApi';
import { btnGhost, inputCls } from './WorkUi';

export function ProcessPicker({ projectId, value, onChange, canCreate = true, disabled, label = 'Proceso' }: {
  projectId: string; value: string; onChange: (id: string) => void; canCreate?: boolean; disabled?: boolean; label?: string;
}) {
  const [items, setItems] = useState<ProcessRow[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setItems((await workApi.processes(projectId)).processes.filter((p) => p.status === 'ACTIVE')); setErr(null); }
    catch { setItems([]); setErr('No se pudieron cargar los procesos'); }
  }, [projectId]);
  useEffect(() => { setItems(null); void load(); }, [load]);
  const create = async () => {
    if (!name.trim()) return;
    try {
      const p = await workApi.createProcess(projectId, { name: name.trim() });
      setName(''); setAdding(false); await load(); onChange(p.id);
    } catch (e) {
      const m = (e as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setErr(Array.isArray(m) ? m.join('. ') : m ?? 'No se pudo crear el proceso');
    }
  };
  return (
    <div className="space-y-1.5" data-testid="process-picker">
      <label className="block text-xs font-medium text-on-surface-variant">{label} <span className="text-[#F87171]">*</span></label>
      <select className={inputCls} value={value} disabled={disabled || items === null} onChange={(e) => onChange(e.target.value)} data-testid="process-select" aria-label={label}>
        <option value="">{items === null ? 'Cargando…' : items.length ? 'Selecciona un proceso' : 'Sin procesos: crea el primero'}</option>
        {(items ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {canCreate && !adding && <button type="button" className={`${btnGhost} text-xs`} onClick={() => setAdding(true)} data-testid="process-add">+ Nuevo proceso</button>}
      {adding && (
        <div className="flex flex-wrap gap-2">
          <input className={`${inputCls} min-w-0 flex-1`} placeholder="Nombre del proceso" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} data-testid="process-new-name" />
          <button type="button" className={btnGhost} onClick={create} data-testid="process-new-save">Crear</button>
          <button type="button" className={btnGhost} onClick={() => { setAdding(false); setName(''); }}>Cancelar</button>
        </div>
      )}
      {err && <p className="text-xs text-[#F87171]" role="alert">{err}</p>}
    </div>
  );
}

/** Chip de Process; sin Process = «Pendiente de clasificar» (nunca se inventa). */
export function ProcessChip({ process }: { process?: { name: string } | null }) {
  if (process) return <span data-testid="process-chip" title="Proceso" className="inline-block rounded-full bg-surface-variant px-2 py-0.5 text-[10px] font-medium text-on-surface-variant">{process.name}</span>;
  return <span data-testid="process-pending" title="Tarea sin proceso asignado (legacy)" className="inline-block rounded-full border border-[#F59E0B]/50 bg-[#F59E0B]/10 px-2 py-0.5 text-[10px] font-medium text-[#B45309] dark:text-[#FBBF24]">Pendiente de clasificar</span>;
}

/** Etiqueta del tipo de proyecto: Externo · Interno - Desarrollo/Administración/Marketing · Pendiente de clasificar. */
export function ProjectTypeBadge({ label, type }: { label?: string; type?: string }) {
  if (!label) return null;
  const tone = type === 'PENDING_CLASSIFICATION' ? 'border border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#B45309] dark:text-[#FBBF24]' : type === 'INTERNAL' ? 'bg-[#6366F1]/10 text-[#4F46E5] dark:text-[#A5B4FC]' : 'bg-surface-variant text-on-surface-variant';
  return <span data-testid="project-type" className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`}>{label}</span>;
}
